import { Box, NumberField, SelectField, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Sparkles } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useCheckSubscriptionItemsInstalled } from 'modules/marketplace_modules/hooks/useChecksubscriptionItemsInstalled'
import PriceInfo from 'modules/upsell/components/SelfUpsell/CheckoutModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type VariantSelectorProps = {
  addon: Plan
  crossSellingAddons?: Plan[] | undefined
  showPrice: boolean
  setSelectedAddonId: (id: string) => void
}

const VariantSelector = ({
  addon,
  crossSellingAddons,
  showPrice,
  setSelectedAddonId,
}: VariantSelectorProps) => {
  const {
    configPlan,
    setConfigPlan,
    period,
    togglePlanSelect,
    currency,
    allAddons,
    discount,
    setSeats,
    seats,
    currentSeats,
    isExpenses,
    allCrossSellingAddons,
    allMandatoryCrossSellingAddons,
    isOneInstalled,
  } = useSelfUpsellContext()
  const isRecruitment = addon.internalTitle.toLowerCase().includes('recruitment')
  const tieredPrice = addon.pricing.getTieredPrice(period, seats)

  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit
  const handleVariantChange = (variant: VariantsEnum) => {
    if (!addon.isSelected) togglePlanSelect(addon)

    addon.setActiveVariant(variant)

    const crossSellingAddonsIds = allCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')

    const mandatoryCrossSellingAddonsIds = allMandatoryCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')

    const addonIds = isExpenses()
      ? addon.pricing.getAllPlanIds(period) || []
      : [addon.pricing.getPlanId(period) || '']

    setConfigPlan({
      addon: [...addonIds, ...crossSellingAddonsIds, ...mandatoryCrossSellingAddonsIds],
      variant: variant,
    })
  }
  const isVariantAvailable = (variant: VariantsEnum) => {
    return addon.variantTypes.includes(variant)
  }

  const recruitmentOptions = allAddons.f25Addons.map((a) => {
    return {
      label: a.title,
      value: a.internalTitle,
    }
  })

  const handlePlanChange = (selectedId: string) => {
    const selectedAddon = allAddons.f25Addons.find((a) => a.internalTitle === selectedId)
    if (!selectedAddon) return

    if (!selectedAddon.isSelected) togglePlanSelect(selectedAddon)

    selectedAddon.setActiveVariant(ENTERPRISE_VARIANT)

    const mandatoryCrossSellingAddonsIds = allMandatoryCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')

    setConfigPlan({
      addon: [selectedAddon.pricing.getPlanId(period) || '', ...mandatoryCrossSellingAddonsIds],
      variant: ENTERPRISE_VARIANT,
    })
    setSelectedAddonId(selectedAddon.internalTitle)
  }

  useEffect(() => {
    const hasEnterprise = addon.variantTypes.includes(ENTERPRISE_VARIANT)
    const hasBusiness = addon.variantTypes.includes(BUSINESS_VARIANT)
    if (!hasEnterprise && !hasBusiness) return

    if (configPlan.variant && addon.variantTypes.includes(configPlan.variant)) return
    if (!addon.isSelected) togglePlanSelect(addon)

    const nextVariant = hasEnterprise ? ENTERPRISE_VARIANT : BUSINESS_VARIANT
    addon.setActiveVariant(nextVariant)
    if (isExpenses()) {
      const enterpriseTieredPrice = addon
        .getSpecificVariant(ENTERPRISE_VARIANT)
        ?.pricing.getTieredPrice(period, seats)
      setSeats(enterpriseTieredPrice?.tiers?.[0]?.endingUnit || 1)
    }

    const addons = [...addon.pricing.getAllPlanIds(period)]
    const crossSellingPlanId = crossSellingAddons
      ? crossSellingAddons
          .map((a) => a.pricing.getPlanId(period) || '')
          .filter((id) => id.includes('one'))
      : []

    if (crossSellingPlanId && !isOneInstalled()) {
      addons.push(...crossSellingPlanId)
    }

    const mandatoryCrossSellingAddonsIds = allMandatoryCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')

    if (mandatoryCrossSellingAddonsIds.length > 0) {
      addons.push(...mandatoryCrossSellingAddonsIds)
    }

    setConfigPlan({
      addon: addons,
      variant: nextVariant,
    })
  }, [])

  const handleExpensesSeatsChange = (value: number) => {
    setSeats(value)

    const mandatoryCrossSellingAddonsIds = allMandatoryCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')

    setConfigPlan({
      addon: [...(addon.pricing.getAllPlanIds(period) || []), ...mandatoryCrossSellingAddonsIds],
      variant: configPlan.variant,
    })

    setSelectedAddonId(addon.internalTitle)
  }

  return (
    <Box gap="s6">
      {isRecruitment && (
        <Box paddingBottom="s8" gap="s6">
          <Text.Normal size="100" color="grey700" weight="medium">
            {i18n.t('self_upsell.first_step.choose_active_job_openings')}
          </Text.Normal>
          <SelectField
            hideLabel
            options={recruitmentOptions}
            value={addon.internalTitle}
            onChange={(value) => handlePlanChange(value)}
            selectValue={addon.internalTitle}
            missingLabel={i18n.t('self_upsell.first_step.choose_active_job_openings')}
          />
        </Box>
      )}
      {isExpenses() && (
        <Box paddingBottom="s8" gap="s6">
          <Text.Normal size="100" color="grey700" weight="medium">
            {i18n.t('self_upsell.first_step.expenses_seat_label')}
          </Text.Normal>
          <NumberField
            locale="en-US"
            onChange={(value) => {
              handleExpensesSeatsChange(value || 0)
            }}
            value={seats}
            min={includedSeatsPerModule || 1}
            max={currentSeats}
            step={1}
            error={
              seats > currentSeats
                ? i18n.t('self_upsell.first_step.expenses_seat_max_error', {
                    maxSeats: currentSeats,
                  })
                : includedSeatsPerModule && seats < includedSeatsPerModule
                ? i18n.t('self_upsell.first_step.expenses_seat_min_error', {
                    minSeats: includedSeatsPerModule,
                  })
                : undefined
            }
            missingLabel={i18n.t('self_upsell.first_step.expenses_seat_label', {
              includedSeatsPerModule,
              currentSeats,
            })}
            selectValue={seats.toString()}
          />
        </Box>
      )}
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('self_upsell.first_step.select_plan_type')}
      </Text.Normal>
      <Box gap="s8">
        {isVariantAvailable(ENTERPRISE_VARIANT) && (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: configPlan.variant === ENTERPRISE_VARIANT ? 'viridian1000' : 'grey400',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={configPlan.variant === ENTERPRISE_VARIANT ? 'viridian100' : 'grey000White'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            onClick={() => handleVariantChange(ENTERPRISE_VARIANT)}
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {ENTERPRISE_VARIANT}
              </Text.Normal>
              <F0TagRaw text="Best value" icon={Sparkles} />
            </Box>
            {showPrice && (
              <PriceInfo
                variant={addon.getSpecificVariant(ENTERPRISE_VARIANT)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={discount}
              />
            )}
          </Box>
        )}
        {isVariantAvailable(BUSINESS_VARIANT) && (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: configPlan.variant === BUSINESS_VARIANT ? 'viridian1000' : 'grey400',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={configPlan.variant === BUSINESS_VARIANT ? 'viridian100' : 'grey000White'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            onClick={() => handleVariantChange(BUSINESS_VARIANT)}
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {BUSINESS_VARIANT}
              </Text.Normal>
            </Box>
            {showPrice && (
              <PriceInfo
                variant={addon.getSpecificVariant(BUSINESS_VARIANT)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={discount}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default VariantSelector
